import {
  Delete,
  Download,
  InsertDriveFile,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigation } from "contexts/notificationsContext";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  downloadDocumento,
  excluirDocumento,
  IDocumento,
  ISubtarefaDetalhada,
  listarDocumentos,
  uploadDocumento,
} from "services/modules/tarefaDetalhe";

interface TabDocumentosProps {
  idTarefa: number;
  subtarefas: ISubtarefaDetalhada[];
  onRefresh: () => void;
  isSubtarefa?: boolean; // Adicionado para identificar se é sub-tarefa
}

export const TabDocumentos = ({
  idTarefa,
  subtarefas,
  onRefresh,
  isSubtarefa = false, // Adicionado
}: TabDocumentosProps) => {
  const { openNotification } = useNavigation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [documentos, setDocumentos] = useState<IDocumento[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================

  const carregarDocumentos = useCallback(async () => {
    setIsLoading(true);
    try {
      // Se é sub-tarefa, buscar por idSubtarefa; caso contrário, por idTarefa
      const data = await listarDocumentos({
        idTarefa: isSubtarefa ? undefined : idTarefa,
        idSubtarefa: isSubtarefa ? idTarefa : undefined,
      });
      setDocumentos(data);
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao carregar documentos",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [idTarefa, isSubtarefa, openNotification]);

  useEffect(() => {
    carregarDocumentos();
  }, [carregarDocumentos]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleOpenModal = () => {
    setOpenModal(true);
    setSelectedFileName("");
    setSelectedFile(null);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedFileName("");
    setSelectedFile(null);
    // Limpar input file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleBuscarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    setSelectedFile(file);
    setSelectedFileName(file.name);
  };

  const handleEnviarDocumento = async () => {
    if (!selectedFile) {
      openNotification({
        isOpen: true,
        message: "Selecione um arquivo para enviar",
        type: "warning",
      });
      return;
    }

    console.log("[TabDocumentos] handleEnviarDocumento - isSubtarefa:", isSubtarefa, "idTarefa:", idTarefa);
    console.log("[TabDocumentos] Enviando:", {
      idTarefaParam: isSubtarefa ? undefined : idTarefa,
      idSubtarefaParam: isSubtarefa ? idTarefa : undefined,
      arquivo: selectedFile.name
    });

    setIsUploading(true);
    try {
      // Se é sub-tarefa, passar idSubtarefa; caso contrário, passar idTarefa
      await uploadDocumento(
        isSubtarefa ? undefined : idTarefa,
        isSubtarefa ? idTarefa : undefined,
        selectedFile
      );
      openNotification({
        isOpen: true,
        message: "Documento enviado com sucesso!",
        type: "success",
      });
      await carregarDocumentos();
      onRefresh();
      handleCloseModal();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao enviar documento",
        type: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (doc: IDocumento) => {
    try {
      await downloadDocumento(doc.idTarefaDocumento, doc.nomeOriginal);
      openNotification({
        isOpen: true,
        message: "Download iniciado!",
        type: "success",
      });
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao baixar documento",
        type: "error",
      });
    }
  };

  const handleDelete = async (idDocumento: number) => {
    if (!window.confirm("Deseja excluir este documento?")) return;

    try {
      await excluirDocumento(idDocumento);
      openNotification({
        isOpen: true,
        message: "Documento excluído com sucesso!",
        type: "success",
      });
      await carregarDocumentos();
      onRefresh();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao excluir documento",
        type: "error",
      });
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box>
      {/* Input file oculto */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileSelect}
      />

      {/* Botão + DOCUMENTO */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          onClick={handleOpenModal}
          sx={{
            bgcolor: "#3C90AD",
            color: "white",
            textTransform: "none",
            fontWeight: "bold",
            "&:hover": {
              bgcolor: "#2d6d83",
            },
          }}
        >
          + DOCUMENTO
        </Button>
      </Box>

      {/* Tabela de Documentos - Conforme mockup: Nome do arquivo, Data Upload, Ações */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.200" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Nome do arquivo</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Data Upload</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }} width={100}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : documentos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box py={4}>
                    <InsertDriveFile
                      sx={{ fontSize: 48, color: "grey.300", mb: 1 }}
                    />
                    <Typography color="textSecondary">
                      Nenhum documento anexado
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              documentos.map((doc) => (
                <TableRow key={doc.idTarefaDocumento} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {doc.nomeOriginal}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(doc.dataUpload).toLocaleDateString("pt-BR")}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        sx={{
                          border: "1px solid #3C90AD",
                          borderRadius: 1,
                          mr: 0.5,
                          color: "#3C90AD",
                        }}
                        onClick={() => handleDownload(doc)}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        sx={{
                          border: "1px solid #f44336",
                          borderRadius: 1,
                          color: "#f44336",
                        }}
                        onClick={() => handleDelete(doc.idTarefaDocumento)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Anexar documento a tarefa */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Anexar documento a tarefa
          <IconButton size="small" onClick={handleCloseModal}>
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button
              variant="contained"
              onClick={handleBuscarClick}
              sx={{
                bgcolor: "#3C90AD",
                color: "white",
                textTransform: "none",
                fontWeight: "bold",
                "&:hover": {
                  bgcolor: "#2d6d83",
                },
              }}
            >
              BUSCAR
            </Button>
            <TextField
              fullWidth
              size="small"
              placeholder="nome do arquivo.pdf"
              value={selectedFileName}
              InputProps={{
                readOnly: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseModal}
            sx={{ textTransform: "none" }}
          >
            CANCELAR
          </Button>
          <Button
            variant="contained"
            onClick={handleEnviarDocumento}
            disabled={!selectedFile || isUploading}
            sx={{
              bgcolor: "#3C90AD",
              color: "white",
              textTransform: "none",
              "&:hover": {
                bgcolor: "#2d6d83",
              },
            }}
          >
            {isUploading ? "ENVIANDO..." : "ENVIAR DOCUMENTO"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
